import { Component, Prop, State, h, Host, Element, Event, EventEmitter } from '@stencil/core';

@Component({
  tag: 'dda-dropdown',
  styleUrls: [],
  shadow: false,
})
export class DdaDropdown {
  @Element() el: HTMLElement;

  @Prop() label: string;
  @Prop() options: string;
  @Prop() selected: string; // can be id or text
  @Prop() disabled: boolean = false;
  @Prop() error?: string;
  @Prop() error_message: string;
  @Prop() helper_text: string;
  @Prop() validation_id: string = 'text';
  @Prop() size?: string;
  @Prop() icon_mode: boolean = false; // New prop for icon mode
  @Prop() custom_class?: string = '';
  @Prop() component_mode?: string;
  @Prop() button_id: string;
  @Prop() toggle_button_name: string;
  @Prop() option_select_button_name: string;
  @Prop() placeholder: string = '';
  @Prop() main_aria_label: string = '';
  
  @State() is_open: boolean = false;
  @State() selectedId: number;
  @State() selectedText: string;
  
  @Event() selectChanged: EventEmitter<{ id: number; text: string }>;

  private get parsedOptions(): { id: number; text: string }[] {
    try {
      return JSON.parse(this.options);
    } catch {
      return [];
    }
  }

  componentWillLoad() {
    const selectedOption = this.parsedOptions.find(
      (opt) => opt.id.toString() === this.selected || opt.text === this.selected
    );
    if (selectedOption) {
      this.selectedId = selectedOption.id;
      this.selectedText = selectedOption.text;
    }
  }

  toggleSelect() {
    if (!this.disabled) {
      this.is_open = !this.is_open;
    }
  }

  selectOption(option: { id: number; text: string }) {
    if (!this.disabled) {
      this.selectedId = option.id;
      this.selectedText = option.text;
      this.is_open = false;
      this.selectChanged.emit(option);
    }
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.is_open && !this.el.contains(event.target as Node)) {
      this.is_open = false;
    }
  };

  connectedCallback() {
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  private numberToWord(num: number): string {
    const words = ['One', 'Two', 'Three', 'Four', 'Five'];
    return words[num - 1] || num.toString();
  }

  render() {
    const labelId = `${this.button_id}-label`;
    const valueId = `${this.button_id}-value`;

    return (
      <Host>
        <div
          class={`dda-input-container ${this.custom_class} ${this.component_mode} ${
            this.disabled ? 'dda-input-disabled' : ''
          } dda-input-size-${this.size} dda-validation-${this.error}`}
        >
          {this.label && (
            <label id={labelId} htmlFor={this.button_id} class="dda-input-label">
              {this.label}
            </label>
          )}
          <div class="dda-dropdown-container">
            <button
              name={this.toggle_button_name}
              id={this.button_id}
              type="button"
              class="dda-input-field dda-dropdown-header"
              onClick={() => this.toggleSelect()}
              aria-labelledby={this.button_id}
              disabled={this.disabled}
            >
              <i class={`three-dots material-icons`} aria-hidden="true">{this.is_open ? 'more_vert' : 'more_vert'}</i>
              {!this.icon_mode && (
              <span id={valueId} class="dda-dropdown-text">
                <span>{this.selectedText || this.placeholder}</span>
                <i class="material-icons">{this.is_open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
              </span>
              )}
              
            </button>

            {this.is_open && (
              <div class="dda-input-dropdown-list dda-select-list">
                {this.parsedOptions.length > 0 ? (
                  this.parsedOptions.map((option, index) => (
                    <button
                      name={`option${this.numberToWord(index + 1)}`}
                      type="button"
                      class={`dda-input-dropdown-item ${this.selectedId === option.id ? 'selected' : ''}`}
                      onClick={() => this.selectOption(option)}
                      role="option"
                      aria-selected={this.selectedId === option.id ? 'true' : 'false'}
                    >
                      {option.text}
                    </button>
                  ))
                ) : (
                  <div class="dda-input-dropdown-item">No options available</div>
                )}
              </div>
            )}
          </div>

          {this.helper_text && (
            <span class="dda-helper-text" id={this.validation_id}>
              {this.helper_text}
            </span>
          )}
          {this.error_message && (
            <span class="dda-error-message" id={this.validation_id}>
              {this.error_message}
            </span>
          )}
        </div>
      </Host>
    );
  }
}
